import { useSyncExternalStore } from "react";

// Registry of documents belonging to open FlexLayout popout windows (PoC #popout).
// Panels register their ownerDocument through PopoutPanelBridge; global features
// that attach window/document listeners (Spotlight, hotkeys, chart keyboard,
// modifier tracking…) subscribe here to cover every window, not just the main one.
//
// Ref-counted: several panels can live in the same popout window.
// Lives in utils (not components/) so chartLib code may depend on it.

// Custom element probe: adopted/connectedCallback are the DOM's only reliable
// signals that a node moved to another document — FlexLayout re-parents portal
// content between windows without remounting React (enableWindowReMount off),
// so no React lifecycle fires on the move. Shared by PopoutPanelBridge and
// useHostDocument.
export const POPOUT_PROBE_TAG = "popout-theme-probe";

if (typeof window !== "undefined" && !customElements.get(POPOUT_PROBE_TAG)) {
  customElements.define(
    POPOUT_PROBE_TAG,
    class extends HTMLElement {
      connectedCallback() {
        this._onDocumentChange?.();
      }
      adoptedCallback() {
        this._onDocumentChange?.();
      }
    },
  );
}

const docCounts = new Map();
const subscribers = new Set();
let snapshot = [];

const EMPTY = [];

function emit() {
  snapshot = [...docCounts.keys()];
  subscribers.forEach((cb) => cb());
}

// Registers a popout document. Returns a release function; call it when the
// panel leaves that document (re-dock, window closed, unmount).
export function acquirePopoutDocument(doc) {
  if (!doc || doc === document) return () => {};
  docCounts.set(doc, (docCounts.get(doc) || 0) + 1);
  if (docCounts.get(doc) === 1) emit();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const count = docCounts.get(doc) || 0;
    if (count <= 1) {
      docCounts.delete(doc);
      emit();
    } else {
      docCounts.set(doc, count - 1);
    }
  };
}

export function getPopoutDocuments() {
  return snapshot.length ? snapshot : EMPTY;
}

function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// React hook: re-renders when the set of popout documents changes.
export function usePopoutDocuments() {
  return useSyncExternalStore(
    subscribe,
    getPopoutDocuments,
    getPopoutDocuments,
  );
}

// Attach a DOM listener to the main document AND every popout document,
// re-attaching as popout windows open/close. Input events (keydown/keyup…)
// land on the document of the window that has focus — a main-only listener
// never sees keys typed over a popout. Returns a detach function.
export function addEventListenerAllWindows(type, handler, options) {
  let detachers = [];
  const attach = () => {
    detachers.forEach((d) => d());
    detachers = [document, ...snapshot].map((doc) => {
      doc.addEventListener(type, handler, options);
      return () => doc.removeEventListener(type, handler, options);
    });
  };
  attach();
  const unsubscribe = subscribe(attach);
  return () => {
    unsubscribe();
    detachers.forEach((d) => d());
    detachers = [];
  };
}
